import axios from 'axios';

// Configuration de base d'Axios
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 10000,
  withCredentials: true,          // 6d-iii : envoi/réception des cookies JWT
  headers: {
    'Content-Type': 'application/json',
  },
});

// Lecture d'un cookie non-httpOnly (les cookies CSRF, posés à Path=/).
const getCookie = (name) => {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
};

// Routes d'authentification où un 401 est une RÉPONSE MÉTIER (identifiants refusés, compte
// non lié, credential Google invalide) et NON le signal d'une session expirée. Y déclencher
// un refresh puis un forceLogout effacerait le message d'erreur par un rechargement de page,
// alors que c'est précisément ce message que le composant appelant doit afficher.
// NB : /auth/refresh n'y figure pas — il est appelé via axios NU, hors de cet intercepteur.
const AUTH_401_IS_BUSINESS = [
  '/auth/login',          // mot de passe erroné, compte désactivé (l'entreprise suspendue sort en 403)
  '/auth/google/login',   // credential invalide, compte non lié (Google ne crée jamais de compte)
  '/auth/google/link',    // credential invalide PENDANT une session valide (profil, 6e-iv)
];

// Mesuré à l'exécution : config.url est le chemin RELATIF passé à api.post ('/auth/login'),
// baseURL vit dans un champ séparé. endsWith couvre en plus une éventuelle URL absolue.
const is401BusinessRoute = (url) => {
  if (!url) return false;
  const path = url.split('?')[0];
  return AUTH_401_IS_BUSINESS.some((route) => path === route || path.endsWith(route));
};

// --- Refresh de session (6c/6d) : une seule requête /refresh à la fois, les 401
// concurrents attendent la même promesse puis se rejouent. ---
let isRefreshing = false;
let refreshQueue = [];

const flushQueue = (error) => {
  refreshQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve()));
  refreshQueue = [];
};

/**
 * URL d'un FICHIER servi par le backend, dérivée du baseURL de l'instance axios.
 *
 * ⚠️ NE PAS relire process.env.REACT_APP_API_URL ailleurs. Sept endroits le faisaient
 * (ProductForm ×2, OrderDetailView ×2, Profile ×2, Header), chacun avec son propre
 * repli `|| 'http://localhost:5000'` — l'ANCIEN port, faux depuis le passage à 5001, et
 * actif sur tout clone sans .env. Deux variantes cohabitaient même :
 *     process.env.X?.replace('/api','') || 'http://localhost:5000'
 *     (process.env.X || 'http://localhost:5000/api').replace('/api','')
 * Ce helper DÉRIVE du baseURL déjà configuré : il n'introduit aucune seconde source de
 * vérité, et un changement de port ne se fait qu'ici.
 *
 * Les fichiers sont servis SOUS `/api`, par `GET /api/files/<path>`. L'ancienne route
 * à la racine a été SUPPRIMÉE — elle servait tout le dossier d'uploads sans
 * authentification ni périmètre. Deux raisons à ce choix de chemin :
 *   · `JWT_ACCESS_COOKIE_PATH` vaut `/api` : le cookie d'authentification n'était pas
 *     envoyé ailleurs, donc aucune route hors de `/api` ne peut identifier l'appelant ;
 *   · la route vérifie que l'appelant a accès au stock PROPRIÉTAIRE du fichier — le
 *     périmètre vient du fichier, pas d'un en-tête qu'une <img> ne peut pas envoyer.
 *
 * C'est une FONCTION et non une base à concaténer, délibérément : le chemin complet est
 * construit ici et nulle part ailleurs, donc aucun appelant n'a de segment à composer —
 * ni à oublier.
 *
 * Rend `undefined` sur un chemin vide, pour que `src={fileUrl(x)}` n'émette aucune
 * requête quand le champ n'est pas renseigné.
 */
export const fileUrl = (path) =>
  path ? `${api.defaults.baseURL}/files/${path}` : undefined;

// Déconnexion best-effort : demande au serveur de vider les cookies, puis purge
// l'état local UI (les tokens ne sont plus en localStorage).
export const performLogout = async () => {
  try {
    const csrf = getCookie('csrf_access_token');
    await axios.post(`${api.defaults.baseURL}/auth/logout`, null, {
      withCredentials: true,
      headers: csrf ? { 'X-CSRF-TOKEN': csrf } : {},
    });
  } catch (e) {
    // best-effort : on purge quoi qu'il arrive
  }
  localStorage.removeItem('user');
  localStorage.removeItem('stocks');
  localStorage.removeItem('currentStock');
  localStorage.removeItem('impersonated');
  localStorage.removeItem('token');          // vestiges éventuels d'une session header
  localStorage.removeItem('refresh_token');
};

// Purge + reload quand il n'y a plus d'issue (refresh impossible/échoué).
// EXPORTÉ depuis UI-6 : la sortie d'impersonation en 403 (impersonateur rétrogradé ou
// désactivé) laisse l'utilisateur COINCÉ dans l'identité de la cible, sans aucun moyen
// de remonter. Une sortie franche est alors la seule issue correcte.
export const forceLogout = () => {
  performLogout().finally(() => window.location.reload());
};

// Intercepteur pour les requêtes
api.interceptors.request.use(
  (config) => {
    const currentStock = localStorage.getItem('currentStock');
    const method = (config.method || 'get').toLowerCase();

    console.log('🌐 API Request:', method.toUpperCase(), config.url);

    // X-Stock-ID : transport applicatif d'isolation (hors JWT, inchangé).
    //
    // 🔴 ON ANALYSE D'ABORD, ON TESTE ENSUITE — ET C'EST TOUT LE DÉFAUT.
    // `localStorage` ne rend que des CHAÎNES : un stock absent y est stocké
    // sous la forme `"null"`, quatre caractères. `if (currentStock)` était donc
    // VRAI, `JSON.parse("null")` rendait `null`, et `.id` levait
    // « Cannot read properties of null ». *Une valeur absente qui se stocke
    // comme une chaîne non vide passe tous les tests de présence.*
    //
    // ⚠️ LE CAS EST NORMAL, PAS EXCEPTIONNEL : le SUPER_ADMIN n'appartient à
    // aucune société et n'a donc AUCUN stock — c'est délibéré, `company_id`
    // est NULL par construction. Le code ne prévoyait tout simplement pas
    // l'utilisateur le plus privilégié du produit.
    //
    // 🔴 ET ÇA NE S'ÉTAIT JAMAIS VU PARCE QUE LE DÉCOR LE CACHAIT : en
    // développement, `localStorage` porte toujours un stock. Le défaut
    // n'apparaît qu'à la première connexion d'un SUPER_ADMIN sur une base
    // NEUVE — c'est-à-dire au premier écran du premier jour d'un déploiement.
    //
    // ⚠️ UNE ABSENCE NORMALE NE SE JOURNALISE PAS. Le `catch` ne garde que le
    // vrai défaut — une chaîne illisible ; un stock légitimement absent passe
    // en silence. *Une erreur affichée à chaque requête pour un cas prévu
    // apprend à ignorer la console*, et c'est là que se perdent les vraies.
    if (currentStock) {
      try {
        const stock = JSON.parse(currentStock);
        if (stock && stock.id != null) {
          config.headers['X-Stock-ID'] = stock.id;
        }
      } catch (e) {
        console.error('   Error parsing currentStock:', e);
      }
    }

    // CSRF double-submit : uniquement sur les méthodes mutantes (les GET sont
    // exempts côté serveur). Le JWT voyage désormais dans le cookie httpOnly.
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      const csrf = getCookie('csrf_access_token');
      if (csrf) config.headers['X-CSRF-TOKEN'] = csrf;
    }

    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Intercepteur pour les réponses
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.config.method?.toUpperCase(), response.config.url, response.status);
    return response;
  },
  async (error) => {
    console.error('❌ API Error:', error.config?.method?.toUpperCase(), error.config?.url, error.response?.status);
    console.error('   Error details:', error.response?.data);

    const originalRequest = error.config;
    const status = error.response?.status;

    // --- 401 : tenter un refresh transparent avant d'abandonner ---
    // (A) Route d'auth : le 401 est une réponse métier -> on la laisse remonter intacte.
    if (status === 401 && originalRequest && !originalRequest._retry
        && !is401BusinessRoute(originalRequest.url)) {
      // Signal de session : le cookie CSRF refresh n'existe que si on est connecté.
      const csrfRefresh = getCookie('csrf_refresh_token');
      if (!csrfRefresh) {
        // (B) Ne purger/recharger QUE si l'app croyait avoir une session. Sur l'écran de
        // connexion il n'y a rien à nettoyer, et recharger effacerait le message d'erreur.
        if (localStorage.getItem('user')) {
          console.error('🔐 401 sans session refresh - Déconnexion');
          forceLogout();
        }
        return Promise.reject(error);
      }

      // Un refresh est déjà en cours : on s'y accroche puis on rejoue.
      if (isRefreshing) {
        originalRequest._retry = true;
        return new Promise((resolve, reject) => refreshQueue.push({ resolve, reject }))
          .then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;
      try {
        // axios NU (pas `api`) → pas de récursion d'intercepteur. Le cookie refresh
        // (Path=/api/auth/refresh) part automatiquement ; CSRF du refresh en header.
        await axios.post(`${api.defaults.baseURL}/auth/refresh`, null, {
          withCredentials: true,
          headers: { 'X-CSRF-TOKEN': csrfRefresh },
        });
        // Le nouveau cookie access est posé par le serveur ; rien à stocker.
        flushQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        console.error('🔐 Refresh échoué - Déconnexion');
        flushQueue(refreshError);
        forceLogout();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    // --- Autres statuts : logging historique inchangé ---
    if (error.response) {
      const { status: st, data } = error.response;

      switch (st) {
        case 403:
          console.error('🚫 Accès interdit:', data.error);
          break;
        case 404:
          console.error('🔍 Ressource non trouvée:', data.error);
          break;
        case 500:
          console.error('💥 Erreur serveur:', data.error);
          break;
        default:
          console.error(`❌ Erreur ${st}:`, data.error);
      }
    } else if (error.request) {
      // Erreur de réseau
      console.error('🌐 Erreur de réseau:', error.request);
    } else {
      // Autre erreur
      console.error('❌ Erreur:', error.message);
    }

    return Promise.reject(error);
  }
);

// Fonctions utilitaires pour les requêtes
export const apiService = {
  // GET
  get: (url, params = {}) => api.get(url, { params }),
  
  // POST
  post: (url, data = {}) => api.post(url, data),
  
  // PUT
  put: (url, data = {}) => api.put(url, data),
  
  // DELETE
  //
  // 🔴 `config` EST TRANSMIS, ET SON ABSENCE A CASSÉ LA SUPPRESSION D'ENTREPRISE.
  // Cet enrobage ne prenait QU'UN paramètre : `apiService.delete(url, { data })`
  // partait avec le second argument **jeté en silence**. Le corps de la requête
  // arrivait `null` au serveur, la confirmation était donc vide, et le refus
  // « La confirmation ne correspond pas » était EXACT — rien ne correspondait,
  // parce que rien n'arrivait.
  //
  // ⚠️ ET LE DIAGNOSTIC ÉVIDENT ÉTAIT FAUX : on soupçonnait axios, qui exige
  // bien `{ data }` dans la CONFIGURATION pour un DELETE. Mais axios n'a jamais
  // été atteint — c'est notre propre enrobage qui perdait l'argument.
  // *Une bibliothèque au comportement inhabituel attire le soupçon ; le code
  // maison qui l'enveloppe y échappe, parce qu'on croit le connaître.*
  //
  // 🔴 UN ENROBAGE QUI ACCEPTE MOINS DE PARAMÈTRES QUE CE QU'IL ENVELOPPE NE
  // LÈVE PAS : JavaScript ignore les arguments en trop. L'appel se compile, se
  // lit correctement, part — et perd sa charge. *Un adaptateur silencieusement
  // plus étroit que son adapté est indétectable au site d'appel.*
  delete: (url, config) => api.delete(url, config),
  
  // Upload de fichier
  upload: (url, formData) => api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
};

export default api;